import { InfoCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Col, Empty, Row, Space, Tooltip } from "antd";
import React, { useCallback, useRef, useState } from "react";
import { useUserAction } from "../../contexts/UserActions";
import { Scene } from "../../generated/types";
import { PanoPosition, PanoView } from "../../types";
import { normaliseYaw } from "../../utils/pano";
import { IssueButtons } from "../ShotIssueButtons";
import styles from "./DualViewer.module.css";
import { SingleViewer } from "./SingleViewer";
import { ViewerTitle } from "./ViewerTitle";
import { ZeroPointingControls } from "./ZeroPointingControls";
import { Shot, ShotViewerProps } from "./types";

type SpotlightCreationToggleProps = {
  value: boolean;
  onToggle: () => void;
};

const SpotlightCreationToggle = ({
  value,
  onToggle,
}: SpotlightCreationToggleProps) => (
  <Tooltip
    placement="topRight"
    title={"turn spotlight creation mode " + (value ? "off" : "on")}
  >
    <Button
      icon={value ? <InfoCircleFilled /> : <InfoCircleOutlined />}
      onClick={onToggle}
    />
  </Tooltip>
);

type DualViewerProps = {
  view: PanoView;
  onChangeView: (view: PanoView) => any;
  comparisonShot?: Shot;
  referenceShot?: Shot;
  onSpotlightUpdate?: () => any;
  focusedSpotlightId?: string;
  onZeroPoint?: (diff: PanoPosition) => any;
  arrowSceneNavigation: (evt: KeyboardEvent) => void;
  sceneCoordinates:
    | Pick<Scene, "id" | "mmX" | "mmY" | "mmZOffset" | "bimDiff">
    | undefined;
  onZeroPointWithBim?: (diff: PanoPosition) => any;
  resetYaw?: (yaw?: number) => void;
};

type DualViewerMode =
  | "default"
  | "spotlightCreation"
  | "zeroPoint"
  | "zeroPointWithBim";

const EMPTY_SHOT_DESCRIPTION = "No shot available";

export const DualViewer = (props: DualViewerProps) => {
  const {
    view,
    onChangeView,
    comparisonShot,
    referenceShot,
    onSpotlightUpdate,
    focusedSpotlightId,
    onZeroPoint,
    arrowSceneNavigation,
    sceneCoordinates,
    onZeroPointWithBim,
    resetYaw,
  } = props;

  const [mode, setMode] = useState<DualViewerMode>("default");
  const [viewOnZeroPoint, setViewOnZeroPoint] = useState<PanoPosition>();
  const [viewOnZeroPointWithBim, setViewOnZeroPointWithBim] =
    useState<PanoPosition>();
  const { canEditJiraIssues } = useUserAction();

  const shots: {
    key: "comparison" | "reference";
    shot: Shot | undefined;
  }[] = [
    { key: "comparison", shot: comparisonShot },
    { key: "reference", shot: referenceShot },
  ];

  const authoritativeViewer = useRef("right");
  const handleChangeView = useCallback(
    (update, viewerId) => {
      if (authoritativeViewer.current === viewerId) {
        onChangeView(update);
      }
    },
    [onChangeView],
  );

  const updateAuthoritativeViewer = useCallback(
    (viewerId) => {
      if (authoritativeViewer.current !== viewerId) {
        authoritativeViewer.current = viewerId;
      }
    },
    [authoritativeViewer],
  );

  const shotViewer = ({ key, shot }: ShotViewerProps) => {
    const isFrozen = mode === "zeroPoint" && key === "comparison";
    return shot === undefined ? (
      <Empty description={EMPTY_SHOT_DESCRIPTION} />
    ) : (
      <div
        key={key}
        className={styles["viewer-content"]}
        onMouseDown={() => updateAuthoritativeViewer(shot.title)}
        onWheel={() => updateAuthoritativeViewer(shot.title)}
      >
        <SingleViewer
          viewerId={shot.title}
          sceneId={shot.scene?.id ?? ""}
          panellumSceneId={shot.imageConfig.imageId}
          imageConfig={shot.imageConfig}
          batch={shot.batch}
          view={view}
          onChangeView={(update) => {
            if (mode === "zeroPoint") {
              setViewOnZeroPoint(update.position);
            } else if (mode === "zeroPointWithBim") {
              setViewOnZeroPointWithBim(update.position);
            } else {
              handleChangeView(update, shot.title);
            }
          }}
          spotlights={shot.spotlights}
          arrows={shot.arrows}
          enableSpotlightCreation={mode === "spotlightCreation"}
          onSpotlightUpdate={() => {
            setMode("default");
            onSpotlightUpdate && onSpotlightUpdate();
          }}
          focusedSpotlightId={focusedSpotlightId}
          frozen={isFrozen}
          showCrosshair={mode === "zeroPoint" || mode === "zeroPointWithBim"}
          pitchBounds={
            mode === "zeroPoint" || mode === "zeroPointWithBim"
              ? { min: 0, max: 0 }
              : undefined
          }
          bimDiff={sceneCoordinates?.bimDiff ?? 0}
        />
      </div>
    );
  };

  const comparisonWindow = (
    <Col key={"comparison"} span={12}>
      <div className={styles["viewer-pane"]}>
        <ViewerTitle title="comparison" />
        {shots.filter((key) => key.key === "comparison").map(shotViewer)}
      </div>
    </Col>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {referenceShot && canEditJiraIssues && (
          <IssueButtons
            arrowSceneNavigation={arrowSceneNavigation}
            shot={referenceShot}
          />
        )}
        <Space className={styles["header-button-group"]}>
          {mode !== "zeroPointWithBim" && (
            <Button
              style={{
                display: "none",
                backgroundColor: view.position.yaw !== 0 ? "red" : "green",
                color: "white",
              }}
              onClick={() => {
                if (view.position.yaw !== 0) {
                  resetYaw?.();
                } else {
                  onChangeView({
                    hfov: view.hfov,
                    position: {
                      yaw: sceneCoordinates?.bimDiff ?? 0,
                      pitch: 0,
                    },
                  });
                }
              }}
            >
              {view.position.yaw !== 0 ? "Reset" : "Look at Current North"}
            </Button>
          )}
          <ZeroPointingControls
            type="zeroPointWithBim"
            isOn={mode === "zeroPointWithBim"}
            onEnter={() => {
              setMode("zeroPointWithBim");
              resetYaw?.(sceneCoordinates?.bimDiff ?? 0);
            }}
            onSave={() => {
              setMode("default");
              const panoDiff = {
                yaw: normaliseYaw((viewOnZeroPointWithBim?.yaw as number) ?? 0),
                // do not update the pitch
                pitch: 0,
              };

              onZeroPointWithBim && onZeroPointWithBim(panoDiff);
              setViewOnZeroPointWithBim(undefined);
            }}
            onDiscard={() => {
              setMode("default");
              // update the view so both sides of the viewer "jump" into sync again
              setViewOnZeroPointWithBim(undefined);
              resetYaw?.(sceneCoordinates?.bimDiff ?? 0);
            }}
          />
          <ZeroPointingControls
            isOn={mode === "zeroPoint"}
            onEnter={() => {
              setMode("zeroPoint");
              resetYaw?.(sceneCoordinates?.bimDiff ?? 0);
            }}
            onSave={() => {
              setMode("default");
              if (viewOnZeroPoint?.yaw !== undefined) {
                const panoDiff = {
                  yaw: (viewOnZeroPoint?.yaw as number) - view.position.yaw,
                  // do not update the pitch
                  pitch: 0,
                };
                onZeroPoint && onZeroPoint(panoDiff);
              }
              setViewOnZeroPoint(undefined);
            }}
            onDiscard={() => {
              setMode("default");
              // update the view so both sides of the viewer "jump" into sync again
              resetYaw?.(sceneCoordinates?.bimDiff ?? 0);
              setViewOnZeroPoint(undefined);
            }}
          />
          <SpotlightCreationToggle
            value={mode === "spotlightCreation"}
            onToggle={() =>
              setMode((prev) =>
                prev === "default" ? "spotlightCreation" : "default",
              )
            }
          />
        </Space>
      </div>
      <Row gutter={16} className={styles.viewer}>
        {comparisonWindow}
        <Col key={"reference"} span={12}>
          <div className={styles["viewer-pane"]}>
            <ViewerTitle title={"reference"} />
            {shots.filter((key) => key.key === "reference").map(shotViewer)}
          </div>
        </Col>
      </Row>
    </div>
  );
};
